/* DefendantData — /admin/defendants
   - Paginated list with server-side search
   - Expandable address rows rendered inline (React Fragment per defendant)
   - Email shown on address rows
   - defendant_id passed from parent when editing address
   - Inline column editing for defendant name */
import { useState, Fragment } from 'react';
import api from '../../../services/api';
import css from '../AdminPage.module.css';
import Badge from '../../../components/ui/Badge/Badge';
import EmptyState from '../../../components/ui/EmptyState/EmptyState';
import SearchInput from '../../../components/ui/SearchInput/SearchInput';
import Pagination from '../../../components/ui/Pagination/Pagination';
import {
  useDefendants,
  useCreateDefendant,
  useUpdateDefendant,
  useDeleteDefendant,
  useSaveDefendantAddress,
  useDeleteDefendantAddress,
} from '../../../hooks/useAdmin';
import { useToast } from '../../../hooks/useToast';
import { usePermissions } from '../../../hooks/usePermissions';
import type { Defendant, DefendantAddress } from '../../../types/admin';

export default function DefendantData() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const ROWS_PER_PAGE = 15;
  const { data, isLoading } = useDefendants(page, search, ROWS_PER_PAGE);
  const defendants = data?.defendants ?? [];
  const pagination = data?.pagination ?? { page: 1, limit: ROWS_PER_PAGE, total: 0, totalPages: 1 };

  const createDefendant = useCreateDefendant();
  const updateDefendant = useUpdateDefendant();
  const deleteDefendant = useDeleteDefendant();
  const saveAddress = useSaveDefendantAddress();
  const deleteAddress = useDeleteDefendantAddress();
  const { showToast } = useToast();
  const { isSuperAdmin, hasPermission } = usePermissions();
  const canExport = isSuperAdmin || hasPermission('export_data');

  const [isExporting, setIsExporting] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<Defendant | null>(null);
  const [expanded, setExpanded] = useState<number | null>(null);

  /* inline edit state */
  const [inlineEditId, setInlineEditId] = useState<number | null>(null);
  const [inlineName, setInlineName] = useState('');

  /* form */
  const [dName, setDName] = useState('');

  /* address form */
  const [showAddrModal, setShowAddrModal] = useState(false);
  const [editingAddr, setEditingAddr] = useState<DefendantAddress | null>(null);
  const [addrDefId, setAddrDefId] = useState(0);
  const [aType, setAType] = useState('Claim Form');
  const [aLine1, setALine1] = useState('');
  const [aLine2, setALine2] = useState('');
  const [aLine3, setALine3] = useState('');
  const [aPostcode, setAPostcode] = useState('');
  const [aEmail, setAEmail] = useState('');

  function handleSearch() {
    setPage(1);
    setSearch(searchInput);
  }

  async function handleExport() {
    setIsExporting(true);
    try {
      const params = new URLSearchParams({ limit: '9999' });
      if (search) params.append('search', search);
      const { data } = await api.get(`/api/defendants?${params.toString()}`);
      const rows: Defendant[] = (data.defendants ?? data.data ?? []).map(
        (d: Record<string, unknown>) => ({
          ...d,
          addresses: Array.isArray(d.addresses)
            ? (d.addresses as Record<string, unknown>[]).map((a) => ({
                ...a,
                address_id: a.address_id ?? a.id,
                defendant_id: d.defendant_id,
              }))
            : [],
        }),
      ) as Defendant[];

      const CSV_HEADERS = [
        'defendant_id',
        'defendant_name',
        'email',
        'phone',
        'assigned_cases',
        'address_type',
        'address_line_1',
        'address_line_2',
        'address_line_3',
        'postcode',
        'address_email',
      ];

      const csvEscape = (v: string | number | undefined | null) => {
        if (v == null) return '';
        const s = String(v);
        return s.includes(',') || s.includes('"') || s.includes('\n')
          ? `"${s.replace(/"/g, '""')}"`
          : s;
      };

      const lines: string[] = [CSV_HEADERS.join(',')];
      for (const d of rows) {
        const base = [
          d.defendant_id,
          d.defendant_name,
          d.email ?? '',
          d.phone ?? '',
          d.assigned_cases ?? 0,
        ];
        if (!d.addresses || d.addresses.length === 0) {
          lines.push([...base, '', '', '', '', '', ''].map(csvEscape).join(','));
        } else {
          for (const a of d.addresses) {
            lines.push(
              [
                ...base,
                a.address_type,
                a.address_line_1 ?? '',
                a.address_line_2 ?? '',
                a.address_line_3 ?? '',
                a.postcode ?? '',
                a.email ?? '',
              ]
                .map(csvEscape)
                .join(','),
            );
          }
        }
      }

      const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `defendants${search ? `-${search}` : ''}-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      showToast('Export failed', 'error');
    } finally {
      setIsExporting(false);
    }
  }

  function openNew() {
    setEditing(null);
    setDName('');
    setShowModal(true);
  }

  /* inline edit for defendant name */
  function startInlineEdit(d: Defendant) {
    setInlineEditId(d.defendant_id);
    setInlineName(d.defendant_name);
  }
  async function saveInlineEdit(defId: number) {
    try {
      await updateDefendant.mutateAsync({ defendant_id: defId, defendant_name: inlineName });
      showToast('Name updated', 'success');
    } catch {
      showToast('Failed', 'error');
    }
    setInlineEditId(null);
  }
  function cancelInlineEdit() {
    setInlineEditId(null);
  }

  async function save() {
    try {
      if (editing) {
        await updateDefendant.mutateAsync({
          defendant_id: editing.defendant_id,
          defendant_name: dName,
        });
        showToast('Defendant updated', 'success');
      } else {
        await createDefendant.mutateAsync({ defendant_name: dName });
        showToast('Defendant created', 'success');
      }
      setShowModal(false);
    } catch {
      showToast('Failed to save defendant', 'error');
    }
  }

  async function remove(id: number) {
    if (!confirm('Delete this defendant?')) return;
    try {
      await deleteDefendant.mutateAsync(id);
      showToast('Deleted', 'success');
    } catch {
      showToast('Failed', 'error');
    }
  }

  function openNewAddr(defId: number) {
    setEditingAddr(null);
    setAddrDefId(defId);
    setAType('registered');
    setALine1('');
    setALine2('');
    setALine3('');
    setAPostcode('');
    setAEmail('');
    setShowAddrModal(true);
  }
  function openEditAddr(defId: number, a: DefendantAddress) {
    setEditingAddr(a);
    setAddrDefId(defId); // Always use passed defId — BE doesn't include it on GET
    setAType(a.address_type);
    setALine1(a.address_line_1 || '');
    setALine2(a.address_line_2 || '');
    setALine3(a.address_line_3 || '');
    setAPostcode(a.postcode || '');
    setAEmail(a.email || '');
    setShowAddrModal(true);
  }

  async function saveAddr() {
    try {
      await saveAddress.mutateAsync({
        address_id: editingAddr?.address_id,
        defendant_id: addrDefId,
        address_type: aType,
        address_line_1: aLine1,
        address_line_2: aLine2,
        address_line_3: aLine3,
        postcode: aPostcode,
        email: aEmail,
      });
      showToast('Address saved', 'success');
      setShowAddrModal(false);
    } catch {
      showToast('Failed', 'error');
    }
  }

  async function removeAddr(defId: number, addrId: number) {
    if (!confirm('Delete this address?')) return;
    try {
      await deleteAddress.mutateAsync({ defendant_id: defId, address_id: addrId });
      showToast('Address deleted', 'success');
    } catch {
      showToast('Failed', 'error');
    }
  }

  if (isLoading) {
    return (
      <div className={css.spinner}>
        <div className={css.spinnerDot} />
        <div className={css.spinnerDot} />
        <div className={css.spinnerDot} />
      </div>
    );
  }

  return (
    <>
      <div className={css.section}>
        <div className={css.sectionHeader}>
          <div>
            <h2 className={css.sectionTitle}>🏛️ Defendant / Lender Data</h2>
            <p className={css.sectionSubtitle}>
              {pagination.total} defendant{pagination.total !== 1 ? 's' : ''}
            </p>
          </div>
          <div className={css.toolbar}>
            <SearchInput
              placeholder="Search by name or ID..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            />
            <button className={css.btnSecondary} onClick={handleSearch}>
              Search
            </button>
            {search && (
              <button
                className={css.btnSecondary}
                onClick={() => {
                  setSearch('');
                  setSearchInput('');
                  setPage(1);
                }}
              >
                Clear
              </button>
            )}
            {canExport && (
              <button className={css.btnSecondary} onClick={handleExport} disabled={isExporting}>
                {isExporting ? 'Exporting…' : '⬇ Export CSV'}
              </button>
            )}
            <button className={css.btnPrimary} onClick={openNew}>
              + New Defendant
            </button>
          </div>
        </div>

        {defendants.length === 0 ? (
          <EmptyState icon="🏗️" message="No defendants found" />
        ) : (
          <>
            <table className={css.table}>
              <thead>
                <tr>
                  <th style={{ width: 32 }}></th>
                  <th>Name</th>
                  <th>Assigned Cases</th>
                  <th>Addresses</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {defendants.map((d) => (
                  <Fragment key={d.defendant_id}>
                    {/* ── Defendant row ── */}
                    <tr
                      style={{ cursor: 'pointer' }}
                      onClick={() =>
                        setExpanded(expanded === d.defendant_id ? null : d.defendant_id)
                      }
                    >
                      <td>
                        <span
                          style={{
                            fontSize: '0.7rem',
                            display: 'inline-block',
                            transform: expanded === d.defendant_id ? 'rotate(90deg)' : 'none',
                            transition: 'transform 0.15s',
                          }}
                        >
                          ▶
                        </span>
                      </td>
                      <td style={{ fontWeight: 600 }}>
                        {inlineEditId === d.defendant_id ? (
                          <div
                            style={{ display: 'flex', gap: 4, alignItems: 'center' }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <input
                              className={css.formInput}
                              value={inlineName}
                              onChange={(e) => setInlineName(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') saveInlineEdit(d.defendant_id);
                                if (e.key === 'Escape') cancelInlineEdit();
                              }}
                              autoFocus
                              style={{ padding: '2px 6px', fontSize: 'var(--font-size-sm)' }}
                            />
                            <button
                              className={`${css.btnPrimary} ${css.btnSmall}`}
                              onClick={() => saveInlineEdit(d.defendant_id)}
                              style={{ padding: '2px 8px' }}
                            >
                              ✓
                            </button>
                            <button
                              className={`${css.btnSecondary} ${css.btnSmall}`}
                              onClick={cancelInlineEdit}
                              style={{ padding: '2px 8px' }}
                            >
                              ✕
                            </button>
                          </div>
                        ) : (
                          <span
                            onDoubleClick={(e) => {
                              e.stopPropagation();
                              startInlineEdit(d);
                            }}
                            title="Double-click to edit"
                            style={{ cursor: 'text' }}
                          >
                            {d.defendant_name}
                          </span>
                        )}
                      </td>
                      <td>{d.assigned_cases ?? 0}</td>
                      <td>
                        <Badge variant="neutral">{d.addresses?.length ?? 0}</Badge>
                      </td>
                      <td>
                        <div
                          style={{ display: 'flex', gap: 4 }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <button
                            className={`${css.btnSecondary} ${css.btnSmall}`}
                            onClick={() => startInlineEdit(d)}
                          >
                            ✏️
                          </button>
                          <button
                            className={`${css.btnSecondary} ${css.btnSmall}`}
                            onClick={() => openNewAddr(d.defendant_id)}
                          >
                            + Addr
                          </button>
                          <button
                            className={`${css.btnDanger} ${css.btnSmall}`}
                            onClick={() => remove(d.defendant_id)}
                          >
                            Del
                          </button>
                        </div>
                      </td>
                    </tr>

                    {/* ── Expanded address rows ── */}
                    {expanded === d.defendant_id &&
                      (d.addresses ?? []).map((a, idx) => (
                        <tr
                          key={`addr-${d.defendant_id}-${a.address_id ?? idx}`}
                          style={{ background: 'var(--gray-50, #f8fafc)' }}
                        >
                          <td></td>
                          <td
                            colSpan={2}
                            style={{ fontSize: 'var(--font-size-xs)', paddingLeft: 24 }}
                          >
                            <Badge variant="info" style={{ marginRight: 8 }}>
                              {a.address_type}
                            </Badge>
                            {[a.address_line_1, a.address_line_2, a.address_line_3, a.postcode]
                              .filter(Boolean)
                              .join(', ')}
                            {a.email && (
                              <span
                                style={{ marginLeft: 12, color: 'var(--primary-600, #2563eb)' }}
                              >
                                ✉ {a.email}
                              </span>
                            )}
                          </td>
                          <td></td>
                          <td>
                            <div style={{ display: 'flex', gap: 4 }}>
                              <button
                                className={`${css.btnSecondary} ${css.btnSmall}`}
                                onClick={() => openEditAddr(d.defendant_id, a)}
                              >
                                Edit
                              </button>
                              <button
                                className={`${css.btnDanger} ${css.btnSmall}`}
                                onClick={() => removeAddr(d.defendant_id, a.address_id!)}
                              >
                                Del
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}

                    {/* Add address prompt when expanded with no addresses */}
                    {expanded === d.defendant_id && (!d.addresses || d.addresses.length === 0) && (
                      <tr style={{ background: 'var(--gray-50, #f8fafc)' }}>
                        <td></td>
                        <td
                          colSpan={3}
                          style={{
                            fontSize: 'var(--font-size-xs)',
                            color: 'var(--gray-400)',
                            paddingLeft: 24,
                          }}
                        >
                          No addresses —{' '}
                          <button
                            className={`${css.btnSecondary} ${css.btnSmall}`}
                            onClick={() => openNewAddr(d.defendant_id)}
                            style={{ fontSize: 'var(--font-size-xs)' }}
                          >
                            Add one
                          </button>
                        </td>
                        <td></td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>

            {/* Pagination */}
            <Pagination
              page={page}
              totalPages={pagination.totalPages}
              total={pagination.total}
              onPageChange={setPage}
            />
          </>
        )}
      </div>

      {/* Defendant modal (New only — editing is inline) */}
      {showModal && (
        <div className={css.modalBackdrop} onClick={() => setShowModal(false)}>
          <div className={css.modal} onClick={(e) => e.stopPropagation()}>
            <div className={css.modalHeader}>
              <h3 className={css.modalTitle}>New Defendant</h3>
              <button className={css.modalClose} onClick={() => setShowModal(false)}>
                ×
              </button>
            </div>
            <div className={css.modalBody}>
              <div className={css.formGroup}>
                <label className={css.formLabel}>Name</label>
                <input
                  className={css.formInput}
                  value={dName}
                  onChange={(e) => setDName(e.target.value)}
                />
              </div>
            </div>
            <div className={css.modalFooter}>
              <button className={css.btnSecondary} onClick={() => setShowModal(false)}>
                Cancel
              </button>
              <button className={css.btnPrimary} onClick={save} disabled={!dName}>
                Create
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Address modal */}
      {showAddrModal && (
        <div className={css.modalBackdrop} onClick={() => setShowAddrModal(false)}>
          <div className={css.modal} onClick={(e) => e.stopPropagation()}>
            <div className={css.modalHeader}>
              <h3 className={css.modalTitle}>{editingAddr ? 'Edit Address' : 'New Address'}</h3>
              <button className={css.modalClose} onClick={() => setShowAddrModal(false)}>
                ×
              </button>
            </div>
            <div className={css.modalBody}>
              <div className={css.formGroup}>
                <label className={css.formLabel}>Address Type</label>
                <select
                  className={css.formSelect}
                  value={aType}
                  onChange={(e) => setAType(e.target.value)}
                >
                  <option value="Claim Form">Claim Form</option>
                  <option value="LoC">LoC</option>
                  <option value="Service">Service</option>
                </select>
              </div>
              <div className={css.formGroup}>
                <label className={css.formLabel}>Address Line 1</label>
                <input
                  className={css.formInput}
                  value={aLine1}
                  onChange={(e) => setALine1(e.target.value)}
                />
              </div>
              <div className={css.formGroup}>
                <label className={css.formLabel}>Address Line 2</label>
                <input
                  className={css.formInput}
                  value={aLine2}
                  onChange={(e) => setALine2(e.target.value)}
                />
              </div>
              <div className={css.formGroup}>
                <label className={css.formLabel}>Address Line 3</label>
                <input
                  className={css.formInput}
                  value={aLine3}
                  onChange={(e) => setALine3(e.target.value)}
                />
              </div>
              <div className={css.grid2}>
                <div className={css.formGroup}>
                  <label className={css.formLabel}>Postcode</label>
                  <input
                    className={css.formInput}
                    value={aPostcode}
                    onChange={(e) => setAPostcode(e.target.value)}
                  />
                </div>
                <div className={css.formGroup}>
                  <label className={css.formLabel}>Email</label>
                  <input
                    className={css.formInput}
                    type="email"
                    value={aEmail}
                    onChange={(e) => setAEmail(e.target.value)}
                  />
                </div>
              </div>
            </div>
            <div className={css.modalFooter}>
              <button className={css.btnSecondary} onClick={() => setShowAddrModal(false)}>
                Cancel
              </button>
              <button className={css.btnPrimary} onClick={saveAddr} disabled={!aLine1}>
                {editingAddr ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
